import {
  CalendarToday as CalendarTodayIcon,
  HistoryOutlined as HistoryOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Button, Grid, IconButton, Tooltip } from '@mui/material';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { FilterList } from 'src/components/filterList';
import { Footer } from 'src/components/footer/Footer';
import { Header } from 'src/components/header';
import { PublicationModal, SimpleDialog } from 'src/components/modal';
import { Progress } from 'src/components/progress';
import { Snackbar } from 'src/components/snackbar';
import { CustomTableWithPagination } from 'src/components/table/CustomTableWithPagination/CustomTableWithPagination';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { MIN_TABLE_HEIGHT } from 'src/constants';
import { PATHS } from 'src/constants/paths';
import {
  useDeleteLocationGroup,
  useFilterAndSorting,
  useGetAllLayoutType,
  useModal,
  useUpdateLocationGroup,
} from 'src/hooks';
import { useApiErrorNavigator } from 'src/hooks/navigation';
import { IFilter, ILayoutType, ILocationGroup } from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';

import { ActionsContainer } from '../locationListPage/LocationListPage.styled';
import {
  AssignLocationButtonContainer,
  ButtonGroupContainer,
  Container,
  StyledButton,
} from './DetailLocationGroupPage.styled';
import { useLocationGroupEditPage } from './hooks/DetailLocationGroupPage.hook';
import { LocationGroupDetailsHeader } from './modules/locationGroupDetailsHeader/LocationGroupDetailsHeader';

export const DetailLocationGroupPage = () => {
  const {
    open: showLocationGroupDeleteModal,
    onOpenModal: openLocationGroupDeleteModal,
    onCloseModal: closeLocationGroupDeleteModal,
  } = useModal();
  const locationGroupSaveModal = useModal();
  const publishModal = useModal();
  const filters: IFilter[] = [];
  const [deletingStatus, setDeletingStatus] = useState(false);
  const { data: layoutTypes, isLoading: isLoadingLayouts } =
    useGetAllLayoutType();
  const {
    locationGroupId,
    locations,
    initialLocations,
    isLoadingLocations,
    locationGroupName,
    isLoadingLocationGroup,
    locationGroupData,
    error,
    columns,
    setLocationGroupName,
    deleteGroupTooltip,
    totalPages,
    totalCount,
    page,
    handlePageChange,
  } = useLocationGroupEditPage();

  const {
    onChangeKeyword,
    onRemoveKeyword,
    onChangeFilter,
    onReset,
    activeFilters,
  } = useFilterAndSorting(filters, [], true, () => {}, true);

  const deleteLocationGroup = useDeleteLocationGroup();
  const updateLocationGroup = useUpdateLocationGroup();

  const { isViewOnly } = useUserRole();

  const navigate = useApiErrorNavigator(error);

  useEffect(() => {
    closeLocationGroupDeleteModal();
  }, [closeLocationGroupDeleteModal, locationGroupId]);

  const renderActions = (row: {
    location_code: { toString: () => string };
  }) => {
    return (
      <ActionsContainer>
        <CustomTooltip
          title={lang.locationDetails}
          placement="top"
          skipValidateOverflow={true}>
          <IconButton
            aria-label="clock"
            onClick={() =>
              navigate({
                pathname: PATHS.LOCATION_DETAILS(row.location_code.toString()),
              })
            }>
            <InfoOutlinedIcon />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip
          title={lang.locationSchedule}
          placement="top"
          skipValidateOverflow={true}>
          <IconButton
            aria-label="calendar"
            onClick={() =>
              navigate(
                {
                  pathname: PATHS.LOCATION_CALENDAR(
                    row.location_code.toString(),
                  ),
                },
                { state: `${row.location_code}` },
              )
            }>
            <CalendarTodayIcon />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip
          title={lang.locationTimings}
          placement="top"
          skipValidateOverflow={true}>
          <IconButton
            aria-label="clock"
            onClick={() =>
              navigate({
                pathname: PATHS.LOCATION_TIMINGS(row.location_code.toString()),
              })
            }>
            <ScheduleIcon />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip
          title={lang.menuBoardHistory}
          placement="top"
          skipValidateOverflow={true}>
          <IconButton
            aria-label="clock"
            onClick={() =>
              navigate({
                pathname: PATHS.LOCATION_TIME_MACHINE(
                  row.location_code.toString(),
                ),
              })
            }>
            <HistoryOutlinedIcon />
          </IconButton>
        </CustomTooltip>
      </ActionsContainer>
    );
  };

  const onHandleConfirmUpdateLocationGroup = async () => {
    try {
      await updateLocationGroup.mutateAsync({
        id: locationGroupId,
        name: locationGroupName.trim(),
      });

      locationGroupSaveModal.onCloseModal();

      Snackbar.success(
        `${locationGroupName.trim()} ${
          lang.successfullyUpdateNameLocationGroup
        }`,
      );

      if (locations.length > 0) {
        publishModal.onOpenModal();
      }
    } catch {
      Snackbar.error(lang.failedSaveText);
    }
  };

  const onHandleConfirmDeleteLocationGroup = async () => {
    closeLocationGroupDeleteModal();
    try {
      setDeletingStatus(true);
      await deleteLocationGroup.mutateAsync({
        id: locationGroupId,
        name: locationGroupName,
      });
      navigate(PATHS.LOCATION_GROUPS);
      Snackbar.success(`${locationGroupName} ${lang.successfullyDeleted}`);
    } catch {
      Snackbar.error(lang.failedDeleteText);
    } finally {
      setDeletingStatus(false);
    }
  };

  return (
    <Container data-testid="DetailLocationGroupPage">
      {isLoadingLocationGroup ? (
        <Progress />
      ) : (
        <>
          <Header
            actionBar={
              isViewOnly ? (
                <ViewOnlyAlert />
              ) : (
                <ButtonGroupContainer>
                  <AssignLocationButtonContainer
                    onClick={() =>
                      navigate({
                        pathname: PATHS.LOCATION_GROUP_ASSIGN(locationGroupId),
                      })
                    }>
                    <StyledButton startIcon={<AddLocationAltOutlinedIcon />}>
                      {lang.addLocations}
                    </StyledButton>
                  </AssignLocationButtonContainer>

                  <Tooltip placement="left" title={deleteGroupTooltip}>
                    <span>
                      <Button
                        disabled={!isEmpty(initialLocations)}
                        onClick={() => openLocationGroupDeleteModal()}
                        startIcon={<DeleteOutlineIcon />}
                        variant="outlined">
                        {lang.deleteGroup}
                      </Button>
                    </span>
                  </Tooltip>
                </ButtonGroupContainer>
              )
            }
            title={locationGroupName}
            breadcrumbItems={[
              {
                label: lang.locationGroup,
                link: `${PATHS.LOCATION_GROUPS}`,
              },
              {
                label: `${locationGroupData?.name}`,
              },
            ]}
          />
          <LocationGroupDetailsHeader
            locationGroupName={locationGroupName}
            locationGroupData={locationGroupData as ILocationGroup}
            onHandleChange={(value: string) => setLocationGroupName(value)}
            isLoadingLayouts={isLoadingLayouts}
            layoutTypes={layoutTypes as ILayoutType[]}
          />
          <Grid container spacing={6} alignItems="stretch">
            <Grid item xs={12} md={3}>
              <FilterList
                filters={filters}
                onChangeKeyword={onChangeKeyword}
                onRemoveKeyword={onRemoveKeyword}
                onChangeFilter={onChangeFilter}
                showReset
                onReset={onReset}
              />
            </Grid>

            <Grid item xs={12} md={9}>
              {isLoadingLocations ? (
                <Progress />
              ) : (
                <CustomTableWithPagination
                  columns={columns}
                  rows={locations}
                  overrideHeight={MIN_TABLE_HEIGHT}
                  renderActions={renderActions}
                  page={page}
                  emptyMessage={
                    isEmpty(locations) && !isEmpty(activeFilters)
                      ? lang.noResultText
                      : lang.nolocationsAddedText
                  }
                  totalPages={totalPages}
                  totalCount={totalCount}
                  onPageChange={handlePageChange}
                  tableContainerHeight={MIN_TABLE_HEIGHT}
                />
              )}
            </Grid>
          </Grid>
          <SimpleDialog
            actionColor="error"
            open={showLocationGroupDeleteModal}
            cancelText={lang.cancel}
            confirmText={lang.deleteText}
            title={lang.locationGroupDeleteModal.title}
            subTitle={`${lang.deleteModalSubtitle}`}
            onClose={closeLocationGroupDeleteModal}
            onClickCancel={closeLocationGroupDeleteModal}
            modalSize="sm"
            disabled={deletingStatus}
            onClickConfirm={onHandleConfirmDeleteLocationGroup}
          />
          <SimpleDialog
            open={locationGroupSaveModal.open}
            cancelText={lang.cancel}
            confirmText={lang.confirm}
            title={lang.titleTextSave}
            subTitle={lang.subTitlePublishConfirm}
            onClose={locationGroupSaveModal.onCloseModal}
            onClickCancel={locationGroupSaveModal.onCloseModal}
            modalSize="sm"
            onClickConfirm={onHandleConfirmUpdateLocationGroup}
            isLoading={updateLocationGroup.isLoading}
          />
          {publishModal.open && (
            <PublicationModal
              open={publishModal.open}
              locationGroupIDs={[Number(locationGroupId)]}
              onClose={publishModal.onCloseModal}
            />
          )}

          {!isViewOnly && (
            <Footer
              showFooterActions
              hasSubNav
              onCancel={() => navigate(PATHS.LOCATION_GROUPS)}
              onSave={() => locationGroupSaveModal.onOpenModal()}
              disableSave={
                locationGroupName === locationGroupData?.name ||
                !locationGroupName?.trim()
              }
              cancelLabel={lang.cancel}
            />
          )}
        </>
      )}
    </Container>
  );
};
